import React from 'react';
import { X, MousePointer, Hand, Eye, Command, Plus, Save, Download } from 'lucide-react';
import { DungeonTheme } from '../types';

interface HelpModalProps {
    onClose: () => void;
    theme: DungeonTheme;
}

const HelpModal: React.FC<HelpModalProps> = ({ onClose, theme }) => {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
            <div
                className="w-full max-w-2xl bg-[#0a0a0a] border border-stone-800 rounded-lg shadow-2xl overflow-hidden relative"
                style={{ borderColor: `${theme.primary}44`, boxShadow: `0 0 40px ${theme.primary}11` }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-white/5 flex justify-between items-center bg-black/40">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded bg-white/5">
                            <Command size={20} style={{ color: theme.primary }} />
                        </div>
                        <div>
                            <h2 className="font-cinzel text-xl font-bold uppercase tracking-widest text-white">Grimório do Arquiteto</h2>
                            <p className="text-[10px] text-stone-500 uppercase tracking-widest">Guia de Comandos e Atalhos</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/5 rounded-full transition-colors text-stone-500 hover:text-white"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">

                    {/* Seção Mouse */}
                    <section className="space-y-4">
                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest flex items-center gap-2">
                            <MousePointer size={14} /> Controles do Mouse
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-white/5 rounded border border-white/5 flex gap-4 items-start">
                                <div className="mt-1 text-white"><Hand size={18} /></div>
                                <div>
                                    <h4 className="text-[11px] font-bold text-white uppercase mb-1">Revelar / Ocultar</h4>
                                    <p className="text-[10px] text-stone-400 leading-relaxed">Clique com o Botão Direito em um elemento e use o botão manual para controle total da visibilidade para os jogadores.</p>
                                </div>
                            </div>
                            <div className="p-4 bg-white/5 rounded border border-white/5 flex gap-4 items-start">
                                <div className="mt-1 text-white"><Eye size={18} /></div>
                                <div>
                                    <h4 className="text-[11px] font-bold text-white uppercase mb-1">Editar Conteúdo</h4>
                                    <p className="text-[10px] text-stone-400 leading-relaxed">Dê um duplo-clique em uma sala para abrir o editor de conteúdo (Inimigos, Tesouros, Notas).</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Seção Fluxo */}
                    <section className="space-y-4">
                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest flex items-center gap-2">
                            <Command size={14} /> Fluxo de Trabalho
                        </h3>
                        <div className="space-y-3">
                            <div className="flex items-center gap-4 p-3 border-b border-white/5">
                                <div className="p-2 bg-stone-900 rounded"><Plus size={14} className="text-stone-400" /></div>
                                <div className="flex-1">
                                    <p className="text-[11px] text-white font-bold uppercase">Gerar Masmorra</p>
                                    <p className="text-[10px] text-stone-500">Ajuste os sliders na aba de engrenagem e clique em "FORJAR MASMORRA" para criar um novo layout.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 p-3 border-b border-white/5">
                                <div className="p-2 bg-stone-900 rounded"><Save size={14} className="text-stone-400" /></div>
                                <div className="flex-1">
                                    <p className="text-[11px] text-white font-bold uppercase">Salvar Projeto</p>
                                    <p className="text-[10px] text-stone-500">Use o botão <span className="text-white">SALVAR PROJETO JSON</span> para guardar seu progresso e continuá-lo depois.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 p-3 border-b border-white/5">
                                <div className="p-2 bg-stone-900 rounded"><Download size={14} className="text-stone-400" /></div>
                                <div className="flex-1">
                                    <p className="text-[11px] text-white font-bold uppercase">Exportar Mapa</p>
                                    <p className="text-[10px] text-stone-500">Gere uma imagem PNG de alta qualidade para usar em VTTs ou imprimir.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="space-y-4">
                        <section>
                            <h4 className="text-[#c2410c] text-[10px] uppercase tracking-widest mb-2">Comandos de Mestre (Painel Esquerdo)</h4>
                            <ul className="text-zinc-400 space-y-1 text-[11px] font-sans list-disc list-inside">
                                <li><span className="text-zinc-200">Duplo Clique:</span> Cria uma nova câmara (inicia oculta por padrão).</li>
                                <li><span className="text-zinc-200">Shift + Clique:</span> Link Mode. Conecte <span className="text-orange-600/80">Sala com Sala</span> ou <span className="text-orange-600/80">Sala com Corredor</span> para criar bifurcações.</li>
                                <li><span className="text-zinc-200">Shift + Hover:</span> Revela instantaneamente a descrição e inimigos em um tooltip flutuante.</li>
                                <li><span className="text-zinc-200">Arraste de Sala:</span> Move câmaras e reconecta caminhos automaticamente.</li>
                            </ul>
                        </section>

                        <section>
                            <h4 className="text-[#c2410c] text-[10px] uppercase tracking-widest mb-2">Interação com Elementos</h4>
                            <ul className="text-zinc-400 space-y-1 text-[11px] font-sans list-disc list-inside">
                                <li><span className="text-zinc-200">Botão Direito (Sala):</span> Edita status, inimigos, ícone e permite "Explodir" (deletar).</li>
                                <li><span className="text-zinc-200">Botão Direito (Caminho):</span> Altera eventos e permite "Desmoronar" (deletar).</li>
                                <li><span className="text-zinc-200">Revelar/Ocultar:</span> Use os botões dentro do menu de cada item para controle de visibilidade dos jogadores.</li>
                            </ul>
                        </section>

                        <section>
                            <h4 className="text-[#c2410c] text-[10px] uppercase tracking-widest mb-2">Editor Avançado</h4>
                            <ul className="text-zinc-400 space-y-1 text-[11px] font-sans list-disc list-inside">
                                <li><span className="text-zinc-200">Bifurcações:</span> No Link Mode, clique em um corredor e depois em uma sala para criar um ponto de junção invisível.</li>
                                <li><span className="text-zinc-200">Exclusão Granular:</span> Clique com o botão direito em um corredor e escolha "Apagar Bloco" para deletar apenas aquela célula.</li>
                            </ul>
                        </section>
                    </div>

                    <div className="p-4 bg-yellow-950/20 border border-yellow-900/30 rounded">
                        <p className="text-[10px] text-yellow-600/80 text-center italic">
                            "A escuridão é sua aliada, mas a luz é sua ferramenta. Controle o que os jogadores veem."
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-black/60 border-t border-white/5 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-stone-100 hover:bg-white text-black text-[10px] font-bold uppercase tracking-widest transition-colors rounded"
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HelpModal;
